/**
 * Current BUDOVA ↔ ROZVADEC links for a polygon (Import → Návaznosti table).
 * Built from the same records as JSON import matching (buildings + rozvaděč FK).
 */

import { formatBudovaAddressLabel, formatBudovaCpLabel } from './budova-address-label.util';
import type { NavaznostBuildingRecord, NavaznostCabinetRecord } from './budova-rozvadec-navaznost.util';
import { formatRozvadecLabel } from './rozvadec-label.util';

export type NavaznostBuildingLinkStatus = 'linked' | 'missing' | 'conflict';

export interface NavaznostBuildingLinkRow {
  objectId: string;
  buildingLabel: string;
  cp: string;
  ropid: string;
  parcelNumber: string;
  rozvadecId: string | null;
  rozvadecLabel: string;
  status: NavaznostBuildingLinkStatus;
}

export interface NavaznostBuildingLinkList {
  total: number;
  linked: number;
  missing: number;
  conflict: number;
  rows: NavaznostBuildingLinkRow[];
}

const STATUS_ORDER: Record<NavaznostBuildingLinkStatus, number> = {
  missing: 0,
  conflict: 1,
  linked: 2,
};

function buildingAddressInput(building: NavaznostBuildingRecord) {
  return {
    addressPoint: {
      cisloPopisne: building.cisloPopisne,
      typCisla: building.typCisla,
      orientacni: building.orientacni,
      ulice: building.ulice,
      obec: building.obec,
      psc: building.psc,
    },
    acquisitionAddress: {
      cisloPopisne: building.acquisitionCisloPopisne,
      typCisla: building.acquisitionTypCisla,
      ulice: building.acquisitionUlice,
      mesto: building.acquisitionMesto,
      parcelNumber: building.acquisitionParcelNumber,
    },
    parcelNumber: building.parcelNumber,
  };
}

export function formatNavaznostBuildingLabel(building: NavaznostBuildingRecord): string {
  const id = (building.id ?? '').trim();
  return formatBudovaAddressLabel(buildingAddressInput(building)) || id;
}

export function navaznostBuildingLinkStatusLabel(status: NavaznostBuildingLinkStatus): string {
  switch (status) {
    case 'linked':
      return 'spárováno';
    case 'conflict':
      return 'konflikt';
    default:
      return 'chybí';
  }
}

export function summarizeNavaznostBuildingLinks(
  rows: NavaznostBuildingLinkRow[],
): NavaznostBuildingLinkList {
  return {
    total: rows.length,
    linked: rows.filter((row) => row.status === 'linked').length,
    missing: rows.filter((row) => row.status === 'missing').length,
    conflict: rows.filter((row) => row.status === 'conflict').length,
    rows,
  };
}

export function buildNavaznostBuildingLinkRows(
  buildings: NavaznostBuildingRecord[],
  cabinets: NavaznostCabinetRecord[],
): NavaznostBuildingLinkRow[] {
  const cabinetsById = new Map<string, NavaznostCabinetRecord>();
  for (const cabinet of cabinets) {
    const id = cabinet.id?.trim();
    if (!id) continue;
    cabinetsById.set(id, cabinet);
  }

  const rows: NavaznostBuildingLinkRow[] = [];
  for (const building of buildings) {
    const objectId = (building.id ?? '').trim();
    if (!objectId) continue;
    const addressInput = buildingAddressInput(building);
    const storedId = (building.previousRozvadecId ?? '').trim();
    const storedName = (building.previousRozvadecName ?? '').trim();
    const base = {
      objectId,
      buildingLabel: formatNavaznostBuildingLabel(building),
      cp: formatBudovaCpLabel(addressInput),
      ropid: (building.ropid ?? '').trim(),
      parcelNumber: (building.parcelNumber ?? building.acquisitionParcelNumber ?? '').trim(),
    };

    if (!storedId) {
      rows.push({
        ...base,
        rozvadecId: null,
        rozvadecLabel: '',
        status: 'missing',
      });
      continue;
    }

    const cabinet = cabinetsById.get(storedId);
    if (!cabinet) {
      rows.push({
        ...base,
        rozvadecId: storedId,
        rozvadecLabel: formatRozvadecLabel({ id: storedId, name: storedName }) || storedId,
        status: 'conflict',
      });
      continue;
    }

    rows.push({
      ...base,
      rozvadecId: cabinet.id,
      rozvadecLabel: formatRozvadecLabel(cabinet) || cabinet.name || cabinet.id,
      status: 'linked',
    });
  }

  return rows.sort((a, b) => {
    const byStatus = STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
    if (byStatus !== 0) return byStatus;
    return a.buildingLabel.localeCompare(b.buildingLabel, 'cs');
  });
}
